// validations.js
import {
  required,
  requiredIf,
  requiredUnless,
  minValue,
  maxValue,
  minLength,
  maxLength,
  email,
  sameAs,
  url,
  numeric,
  decimal,
  integer,
  between,
  not,
  helpers
} from '@vuelidate/validators'

// 🔍 Evalúa una condición simple como A > 5
function evaluarCondicion(valorCampo, operador, valorEsperado) {
  switch (operador) {
    case '==': return valorCampo === valorEsperado
    case '!=': return valorCampo !== valorEsperado
    case '>': return valorCampo > valorEsperado
    case '<': return valorCampo < valorEsperado
    case '>=': return valorCampo >= valorEsperado
    case '<=': return valorCampo <= valorEsperado
    default: return false
  }
}

// 🔁 Reemplaza variables dinámicas como :min, :max, :value en los mensajes
function parseMensaje(msg, ctx) {

  console.log(msg, ctx);

  return msg
    ?.replace(':minValue', ctx.$params?.min)
    .replace(':maxValue', ctx.$params?.max)
    .replace(':value', ctx.$model)
    .replace(':property', ctx?.$property ?? '')
}

// 🎯 Función principal exportable
export function generarReglasDesdeJSON(fields, reglasJSON, state) {
  const reglas = {}

  for (const field of fields) {
    const nombreCampo = field.name
    const tipoCampo = field.type
    const reglasCampo = reglasJSON.find(r => r.field === nombreCampo)
    reglas[nombreCampo] = {}

    // ✅ Validación automática por tipo
    if (tipoCampo === 'Number') {
      reglas[nombreCampo].isNumber = helpers.withMessage(
        `El campo ${nombreCampo} debe ser numérico`,
        value => !value || !isNaN(value)
      )
    }

    if (tipoCampo === 'Date') {
      reglas[nombreCampo].isDate = helpers.withMessage(
        `El campo ${nombreCampo} debe ser una fecha válida`,
        value => !value || !isNaN(new Date(value).getTime())
      )
    }

    if (tipoCampo === 'Boolean') {
      reglas[nombreCampo].isBoolean = helpers.withMessage(
        `El campo ${nombreCampo} debe ser verdadero o falso`,
        value => value === true || value === false
      )
    }

    if (reglasCampo?.rules) {
      for (const rule of reglasCampo.rules) {
        const msg = rule.message

        switch (rule.type) {
          case 'required':
            reglas[nombreCampo].required = helpers.withMessage(ctx => parseMensaje(msg, ctx), required)
            break

          case 'requiredIf':
            reglas[nombreCampo].required = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              requiredIf(() =>
                evaluarCondicion(state.data[rule.field], rule.operator, rule.value)
              )
            )
            break

          case 'requiredUnless':
            reglas[nombreCampo].required = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              requiredUnless(() =>
                evaluarCondicion(state.data[rule.field], rule.operator, rule.value)
              )
            )
            break

          case 'requiredIfAll':
            reglas[nombreCampo].required = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              requiredIf(() =>
                rule.conditions.every(c =>
                  evaluarCondicion(state.data[c.field], c.operator, c.value)
                )
              )
            )
            break

          case 'requiredIfAny':
            reglas[nombreCampo].required = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              requiredIf(() =>
                rule.conditions.some(c =>
                  evaluarCondicion(state.data[c.field], c.operator, c.value)
                )
              )
            )
            break

          case 'requiredIfGroup':
            reglas[nombreCampo].required = helpers.withMessage(
              msg,
              requiredIf(() =>
                rule.groups.some(group =>
                  group.every(c =>
                    evaluarCondicion(state.data[c.field], c.operator, c.value)
                  )
                )
              )
            )
            break

          case 'minValue':
            reglas[nombreCampo].minValue = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              minValue(Number(rule.value))
            )
            break

          case 'maxValue':
            reglas[nombreCampo].maxValue = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              maxValue(Number(rule.value))
            )
            break

          case 'between':
            reglas[nombreCampo].between = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              between(Number(rule.min), Number(rule.max))
            )
            break

          case 'minLength':
            reglas[nombreCampo].minLength = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              minLength(Number(rule.value))
            )
            break

          case 'maxLength':
            reglas[nombreCampo].maxLength = helpers.withMessage(
              ctx => parseMensaje(msg, ctx),
              maxLength(Number(rule.value))
            )
            break

          case 'email':
            reglas[nombreCampo].email = helpers.withMessage(msg, email)
            break

          case 'url':
            reglas[nombreCampo].url = helpers.withMessage(msg, url)
            break

          case 'numeric':
            reglas[nombreCampo].numeric = helpers.withMessage(msg, numeric)
            break

          case 'decimal':
            reglas[nombreCampo].decimal = helpers.withMessage(msg, decimal)
            break

          case 'integer':
            reglas[nombreCampo].integer = helpers.withMessage(msg, integer)
            break

          case 'sameAs':
            reglas[nombreCampo].sameAs = helpers.withMessage(
              msg,
              sameAs(() => state.data[rule.field])
            )
            break

          case 'not': {
            let inner = null
            switch (rule.of?.type) {
              case 'email': inner = email; break
              case 'url': inner = url; break
              case 'numeric': inner = numeric; break
              case 'decimal': inner = decimal; break
              case 'integer': inner = integer; break
              default:
                console.warn(`Regla desconocida en 'not': ${rule.of?.type}`)
            }
            if (inner) {
              reglas[nombreCampo].not = helpers.withMessage(msg, not(inner))
            }
            break
          }
        }
      }
    }
  }

  return reglas
}
